import { graphql, readInlineData } from 'react-relay';
import { createStore } from 'react-sweet-state';

import { default as timezoneHolder } from '@townsquare/date-utils/timezone-holder';
import { sessionStorageFacade } from '@townsquare/facade';

import { storeHomeWorkspace$key } from './__generated__/storeHomeWorkspace.graphql';
import { storeWorkspace$key } from './__generated__/storeWorkspace.graphql';
import { WorkspaceDetails } from './types';

// deepcode ignore HardcodedNonCryptoSecret: this is not a secret
export const WORKSPACE_CLOUDID_SESSION_STORAGE_KEY = 'team-central-cloud-id';

const homeWorkspaceFragment = graphql`
  fragment storeHomeWorkspace on WorkspaceSummary @inline {
    id
    uuid
    name
    keyPrefix
    timezone
    cloudId
    goalScoringMode
    type
    activationId
    navV4Enrollment
  }
`;

const workspaceFragment = graphql`
  fragment storeWorkspace on WorkspaceByContextSuccessfulResponse @inline {
    workspace {
      id
      uuid
      name
      keyPrefix
      timezone
      cloudId
      organisationId
      goalScoringMode
      type
      # eslint-disable-next-line relay/unused-fields
      activationId
      userIsOrgAdmin
      userHasInvitePermission
    }
    workspaceCount
  }
`;

const trySetPreferredWorkspace = (userId: string, cloudId = '') => {
  try {
    sessionStorageFacade?.setItem(`${WORKSPACE_CLOUDID_SESSION_STORAGE_KEY}-${userId}`, cloudId);
  } catch {
    // Skip if we can't do this (session storage not available)
  }
};

export const workspaceStore = createStore({
  initialState: {} as WorkspaceDetails,
  actions: {
    initWorkspaceFromGraph:
      (workspaceRef: storeWorkspace$key, userId: string) =>
      ({ setState }) => {
        const data = readInlineData(workspaceFragment, workspaceRef);
        const workspace = data?.workspace;

        if (!workspace) {
          return;
        }

        const newState: WorkspaceDetails = {
          ...workspace,
          globalId: workspace.id,
          UUID: workspace.uuid,
          numOfActiveWorkspaces: data.workspaceCount ?? 1,
          capabilities: {
            orgAdmin: workspace.userIsOrgAdmin,
            nav4Admin: false,
            inviteToWorkspace: workspace.userHasInvitePermission ?? false,
          },
          goalScoringMode: workspace.goalScoringMode,
          identifier: workspace.keyPrefix,
          type: workspace.type,
        };
        timezoneHolder.setWorkspaceTimezone(workspace.timezone);
        setState(newState);
        trySetPreferredWorkspace(userId, workspace.cloudId);
        return newState;
      },
    initWorkspaceFromHome:
      (
        workspaceRef: storeHomeWorkspace$key,
        metadata: {
          numWorkspaces: number;
          userIsOrgAdmin: boolean;
          organisationId: string;
          userIsNav4Admin?: boolean;
        },
      ) =>
      ({ setState }) => {
        const workspace = readInlineData(homeWorkspaceFragment, workspaceRef);
        if (!workspace) {
          return;
        }

        const newState: WorkspaceDetails = {
          ...workspace,
          globalId: workspace.id,
          UUID: workspace.uuid,
          activationId: workspace.activationId,
          identifier: workspace.keyPrefix,
          isSiteEnrolledToNav4: workspace.navV4Enrollment === 'ENROLLED',
          numOfActiveWorkspaces: metadata.numWorkspaces,
          organisationId: metadata.organisationId,
          capabilities: {
            orgAdmin: metadata.userIsOrgAdmin,
            nav4Admin: !!metadata.userIsNav4Admin,
            inviteToWorkspace: false,
          },
        };
        timezoneHolder.setWorkspaceTimezone(workspace.timezone);
        setState(newState);

        return newState;
      },
    initWorkspace:
      ({
        globalId,
        name,
        UUID,
        keyPrefix,
        timezone,
        cloudId,
        organisationId,
        goalScoringMode,
        numOfActiveWorkspaces,
        capabilities,
        type,
        identifier,
        activationId,
      }: WorkspaceDetails) =>
      ({ setState }) => {
        const newState = {
          globalId,
          UUID,
          name,
          keyPrefix,
          timezone,
          cloudId,
          organisationId,
          goalScoringMode,
          numOfActiveWorkspaces,
          capabilities,
          type,
          identifier,
          activationId,
        };
        timezoneHolder.setWorkspaceTimezone(timezone);
        setState(newState);
        return newState;
      },
    setWorkspaceTimezone:
      timezone =>
      async ({ setState }) => {
        timezoneHolder.setWorkspaceTimezone(timezone);
        setState({ timezone });
      },
    setWorkspaceGoalScoringMode:
      goalScoringMode =>
      async ({ setState }) => {
        setState({ goalScoringMode });
      },
  },
  name: 'workspace-store',
});
